import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip
} from '@mui/material';
import { updateDoc, doc, getDoc, collectionGroup } from 'firebase/firestore';
import {
  Search, Edit, Delete, Visibility, Add, ZoomIn, ZoomOut, Image as ImageIcon,
  RotateLeft, RotateRight, Download, ArrowBackIosNew, ArrowForwardIos,
  Info as InfoIcon, CheckCircle, Cancel, VerifiedUser, HourglassEmpty
} from '@mui/icons-material';
import { collection, getDocs, deleteDoc, query, where } from 'firebase/firestore';
import { firestore } from '../../../firebase.config';
import { useAuth } from '../../../AuthContext';
import { useUserContext } from '../../../components/context/UserContext';




const currencies = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' }
];

// Map expense types to a color palette (hex or MUI palette keys)
const expenseTypeColors = {
  Travel: '#1976d2', // blue
  Meal: '#8e24aa', // purple
  'Office Supplies': '#00796b', // teal
  Utilities: '#ef6c00', // orange
  Rent: '#6d4c41', // brown
  Transportation: '#023f60ff',
  'Internet/Broadband': '#7bb7d7ff',
  Event: '#512da8',
  Medical: '#d32f2f',
  Education: '#2e7d32',
  Entertainment: '#f06292',
  Shopping: '#ff8a65',
  Subscription: '#5c6bc0',
  Gift: '#f9a825',
  Other: '#9e9e9e'
};

const getTypeColor = (type) => {
  if (!type) return expenseTypeColors['Other'];
  // Try exact match then try casing-insensitive match
  if (expenseTypeColors[type]) return expenseTypeColors[type];
  const foundKey = Object.keys(expenseTypeColors).find(k => k.toLowerCase() === String(type).toLowerCase());
  return foundKey ? expenseTypeColors[foundKey] : expenseTypeColors['Other'];
};

const getCurrencySymbol = (currencyCode) => {
  const currency = currencies.find(c => c.code === currencyCode);
  return currency ? currency.symbol : '₹';
};

// Format date as DD-MON-YYYY (e.g., 23-Sep-2025)
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  const date = typeof dateString === 'object' && dateString.toDate
    ? dateString.toDate()
    : new Date(dateString);

  if (isNaN(date.getTime())) return 'N/A';

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();

  return `${day}-${month}-${year}`.toUpperCase();
};

const ListPage = () => {
  const { currentUser } = useAuth(); // Get currentUser from AuthContext
  const { isAdmin } = useUserContext(); // Get isAdmin from UserContext

  // Handle status change for admin
  const handleStatusChange = async (expenseId, newStatus) => {
    if (!isAdmin) {
      console.log('User is not authorized to change status');
      return;
    }
    
    // Find the expense in local state to check current status
    const expense = expenses.find(e => e.id === expenseId);
    if (!expense) {
      console.error('Expense not found');
      return;
    }

    // Prevent changing status if already approved or rejected
    if (expense.status === 'Approved' || expense.status === 'Rejected') {
      console.log(`Cannot change status: Expense is already ${expense.status}`);
      return;
    }

    console.log('Current user role:', currentUser?.role, 'isAdmin:', isAdmin);

    // If status requires a comment (Approved or Rejected), open comment dialog
    if (newStatus === 'Approved' || newStatus === 'Rejected') {
      setCommentDialog({
        open: true,
        expenseId,
        comment: '',
        status: newStatus
      });
      return;
    }

    // For other statuses, update directly
    await updateStatus(expenseId, newStatus, '');
  };

  // Handle opening the info dialog
  const handleOpenInfo = (expense, e) => {
    e.stopPropagation();
    setInfoDialog({
      open: true,
      expense: expense
    });
  };

  // Handle closing the info dialog
  const handleCloseInfo = () => {
    setInfoDialog(prev => ({ ...prev, open: false }));
  };

  const handleCommentSubmit = async () => {
    const { expenseId, status, comment } = commentDialog;
    if (comment.trim()) {
      await updateStatus(expenseId, status, comment);
      setCommentDialog({
        open: false,
        expenseId: null,
        comment: '',
        status: ''
      });
    } else {
      // If no comment provided, just update the status without a comment
      await updateStatus(expenseId, status, '');
      setCommentDialog({
        open: false,
        expenseId: null,
        comment: '',
        status: ''
      });
    }
  };

  const updateStatus = async (expenseId, newStatus, comment) => {
    if (!isAdmin) {
      console.log('User is not authorized to change status');
      return;
    }

    // If it's a rejection without a comment, use a default message
    const rejectionMessage = newStatus === 'Rejected' && !comment.trim()
      ? 'No reason provided for rejection.'
      : comment;

    try {
      console.log('Updating status for:', expenseId, 'to:', newStatus);

      // Find the expense in local state
      const expense = expenses.find(e => e.id === expenseId);
      if (!expense) {
        console.error('Expense not found in local state');
        return;
      }

      // Determine if this is a voucher or regular expense
      const isVoucher = expense.type === 'voucher';

      try {
        if (isVoucher) {
          // For vouchers, update the specific voucher in its parent document
          const parentDocId = expenseId.split('_voucher_')[0];
          const voucherIndex = expense.voucherIndex || 0;

          // Get the current document first
          const docRef = doc(firestore, 'expense', parentDocId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data();
            const updatedVouchers = [...(data.vouchers || [])];

            if (updatedVouchers[voucherIndex]) {
              updatedVouchers[voucherIndex] = {
                ...updatedVouchers[voucherIndex],
                status: newStatus,
                ...((newStatus === 'Approved' || newStatus === 'Rejected') && {
                  [newStatus === 'Approved' ? 'approvalComment' : 'rejectionComment']: newStatus === 'Rejected' ? rejectionMessage : comment,
                  [newStatus === 'Approved' ? 'approvedAt' : 'rejectedAt']: new Date().toISOString(),
                  [newStatus === 'Approved' ? 'approvedBy' : 'rejectedBy']: currentUser.email
                })
              };

              // Update the document with the modified vouchers array
              await updateDoc(docRef, {
                vouchers: updatedVouchers
              });

              console.log('Voucher status updated successfully');
            } else {
              console.error('Voucher not found at index:', voucherIndex);
              return;
            }
          } else {
            console.error('Parent document not found:', parentDocId);
            return;
          }
        } else {
          // For regular expenses, update the document directly
          const now = new Date().toISOString();
          const updateData = {
            status: newStatus,
            updatedBy: currentUser.email,
            updatedOn: now,
            // Preserve existing updatedBy/updatedOn if they exist
            ...(expense.updatedBy && { updatedBy: expense.updatedBy }),
            ...(expense.updatedOn && { updatedOn: expense.updatedOn })
          };

          if (newStatus === 'Approved' || newStatus === 'Rejected') {
            const commentField = newStatus === 'Approved' ? 'approvalComment' : 'rejectionComment';
            const timeField = newStatus === 'Approved' ? 'approvedAt' : 'rejectedAt';
            const userField = newStatus === 'Approved' ? 'approvedBy' : 'rejectedBy';

            updateData[commentField] = newStatus === 'Rejected' && !comment.trim()
              ? 'No reason provided for rejection.'
              : comment;
            updateData[timeField] = now;
            updateData[userField] = currentUser.email;
          }
          await updateDoc(doc(firestore, 'expense', expenseId), updateData);
          console.log('Expense status updated successfully');
        }

        // Update local state
        setExpenses(prev => prev.map(e =>
          e.id === expenseId ? { ...e, status: newStatus } : e
        ));

      } catch (firestoreError) {
        console.error('Firestore update error:', firestoreError);
        throw firestoreError; // Re-throw to be caught by the outer catch
      }

    } catch (error) {
      console.error('Error updating status:', error);
      // You might want to show an error toast/message to the user here
    }
  };
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('');
  const [claimantFilter, setClaimantFilter] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusOptions, setStatusOptions] = useState(['All', 'Raised', 'Verified', 'Approved', 'Rejected']);
  const [commentDialog, setCommentDialog] = useState({
    open: false,
    expenseId: null,
    comment: '',
    status: ''
  });

  // State for info dialog
  const [infoDialog, setInfoDialog] = useState({
    open: false,
    expense: null
  });

  // Update status options when expenses change
  useEffect(() => {
    if (expenses.length > 0) {
      // Get all unique statuses from the current expenses
      const statuses = new Set(expenses.map(expense => expense.status).filter(Boolean));

      // Define the desired status order
      const defaultStatuses = ['Raised', 'Verified', 'Approved', 'Rejected'];

      // Add default statuses in the specified order
      const allStatuses = ['All', ...defaultStatuses];

      // Add any additional statuses that might come from the database
      defaultStatuses.forEach(status => statuses.add(status));

      // Only update if there are new statuses
      setStatusOptions(prevOptions => {
        const newOptions = [...new Set([...allStatuses, ...Array.from(statuses)])];
        return JSON.stringify(prevOptions) !== JSON.stringify(newOptions)
          ? newOptions
          : prevOptions;
      });
    }
  }, [expenses]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return { bgcolor: '#e8f5e9', color: '#2e7d32' }; // Light green
      case 'Rejected':
        return { bgcolor: '#ffebee', color: '#c62828' }; // Light red
      case 'Verified':
        return { bgcolor: '#e3f2fd', color: '#1565c0' }; // Light blue
      case 'Raised':
      default:
        return { bgcolor: '#fbe9e7', color: '#d84315' }; // Light orange
    }
  };
  const [selectedCard, setSelectedCard] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [userInfo, setUserInfo] = useState({
    createdBy: 'Unknown User',
    updatedBy: 'Unknown User',
    createdAt: 'Unknown Date',
    updatedAt: 'Unknown Date'
  });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        setLoading(true);

        // Build queries to find expenses that may have been saved under different fields
        const queriesToRun = [];
        if (currentUser?.uid) {
          queriesToRun.push(query(collection(firestore, 'expense'), where('uid', '==', currentUser.uid)));
          queriesToRun.push(query(collection(firestore, 'expense'), where('createdBy', '==', currentUser.uid)));
        }
        // Also try matching createdBy against the user's email (some docs store email in createdBy)
        if (currentUser?.email) {
          queriesToRun.push(query(collection(firestore, 'expense'), where('createdBy', '==', currentUser.email)));
        }
        // Also include tenantId-based docs if available (multi-tenant setups)
        if (currentUser?.tenantId) {
          queriesToRun.push(query(collection(firestore, 'expense'), where('tenantId', '==', currentUser.tenantId)));
        }
        
        // If current user is an App Admin, also include expenses created by users that this admin added.
        // We look up UserProfileMaster documents where `addedBy` equals the admin's email,
        // then query the expense collection for those users' emails (expense.email).
        if (isAdmin && currentUser?.email) {
          try {
            const usersRef = collection(firestore, 'UserProfileMaster');
            const usersQuery = query(usersRef, where('addedBy', '==', currentUser.email));
            const usersSnap = await getDocs(usersQuery);

            const addedEmails = [];
            usersSnap.forEach(u => {
              const ud = u.data();
              if (ud?.email) addedEmails.push(ud.email);
            });

            // Batch into groups of <=10 for Firestore 'in' queries
            const BATCH_SIZE = 10;
            for (let i = 0; i < addedEmails.length; i += BATCH_SIZE) {
              const batch = addedEmails.slice(i, i + BATCH_SIZE);
              if (batch.length > 0) {
                queriesToRun.push(query(collection(firestore, 'expense'), where('email', 'in', batch)));
              }
            }

            // Also include any expenses where createdBy is the admin (some flows may set this)
            queriesToRun.push(query(collection(firestore, 'expense'), where('createdBy', '==', currentUser.email)));
          } catch (err) {
            console.warn('Error fetching users added by admin:', err);
            // Fallback: include any older claimantAddedBy field on expense
            queriesToRun.push(query(collection(firestore, 'expense'), where('claimantAddedBy', '==', currentUser.email)));
          }
        }

        const docsMap = new Map();

        // If no specific queries could be constructed, fall back to reading the collection (careful in prod)
        if (queriesToRun.length === 0) {
          const snapshot = await getDocs(collection(firestore, 'expense'));
          snapshot.forEach(d => docsMap.set(d.id, d));
        } else {
          for (const q of queriesToRun) {
            try {
              const snap = await getDocs(q);
              snap.forEach(d => docsMap.set(d.id, d));
            } catch (qErr) {
              // ignore single-query failures but log
              console.warn('Expense query failed:', qErr);
            }
          }
        }

        const expensesData = [];

        // Process merged documents
        Array.from(docsMap.values()).forEach((doc) => {
          const data = doc.data();
          // Skip voucher-only entries (they're displayed as part of parent docs)
          if (data.type === 'voucher') return;

          // Process vouchers inside a parent expense
          if (data.vouchers && data.vouchers.length > 0) {
            data.vouchers.forEach((voucher, index) => {
              expensesData.push({
                id: `${doc.id}_voucher_${index}`,
                ...data, // parent data
                ...voucher, // voucher specifics
                type: 'voucher',
                date: formatDate(data.date),
                displayImage: data.imageUrl || (data.imageUrls && data.imageUrls[0]) || null,
                docId: doc.id
              });
            });
          }

          // Add parent expense if it qualifies
          if ((data.amount || (data.imageUrls && data.imageUrls.length > 0)) && data.type !== 'voucher') {
            expensesData.push({
              id: doc.id,
              ...data,
              type: 'expense',
              date: formatDate(data.date || data.displayDate),
              displayImage: data.imageUrl || (data.imageUrls && data.imageUrls[0]) || null,
              docId: doc.id
            });
          }
        });

        // Sort by creation date (newest first)
        expensesData.sort((a, b) => {
          const dateA = new Date(a.createdAt?.toDate?.() || a.createdAt || 0);
          const dateB = new Date(b.createdAt?.toDate?.() || b.createdAt || 0);
          return dateB - dateA;
        });

        setExpenses(expensesData);
      } catch (error) {
        console.error('Error fetching expenses: ', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchExpenses();
    }
  }, [currentUser]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await deleteDoc(doc(firestore, 'expense', id));
        setExpenses(prevExpenses => prevExpenses.filter(expense => expense.id !== id));
      } catch (error) {
        console.error('Error deleting expense:', error);
      }
    }
  };

  const handlePreview = useCallback((card) => {
    // Log card and common image fields for debugging visibility issues (esp. for non-admin users)
    console.log('handlePreview invoked for card:', {
      id: card?.id,
      type: card?.type,
      imageUrl: card?.imageUrl,
      voucherImageUrl: card?.voucherImageUrl,
      imageUrls: card?.imageUrls,
      voucher: card?.voucher,
      vouchers: card?.vouchers
    });
    setSelectedCard(card);
    setPreviewOpen(true);
    setRotation(0);

    // Update user info when previewing
    setUserInfo({
      createdBy: card?.createdBy || currentUser?.email || 'Unknown User',
      updatedBy: card?.updatedBy || card?.createdBy || currentUser?.email || 'Unknown User',
      createdAt: card?.createdAt?.toDate ? formatDate(card.createdAt.toDate()) :
        card?.createdAt ? formatDate(card.createdAt) : 'Unknown Date',
      updatedAt: card?.updatedAt?.toDate ? formatDate(card.updatedAt.toDate()) :
        card?.updatedAt ? formatDate(card.updatedAt) :
          card?.createdAt?.toDate ? formatDate(card.createdAt.toDate()) :
            card?.createdAt ? formatDate(card.createdAt) : 'Unknown Date'
    });
    setZoomLevel(1);
    setCurrentImageIndex(0);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    setZoomLevel(1);
    setRotation(0);
    setCurrentImageIndex(0);
    // Reset user info when closing preview
    setUserInfo({
      createdBy: 'Unknown User',
      updatedBy: 'Unknown User',
      createdAt: 'Unknown Date',
      updatedAt: 'Unknown Date'
    });
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.2, 3)); // Limit max zoom to 3x
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5)); // Limit min zoom to 0.5x
  }, []);

  const handleRotateLeft = useCallback(() => {
    setRotation(prev => (prev - 90) % 360);
  }, []);

  const handleRotateRight = useCallback(() => {
    setRotation(prev => (prev + 90) % 360);
  }, []);

  const handleDownload = useCallback(async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expense-receipt-${new Date().getTime()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  }, []);

  const handleNextImage = useCallback(() => {
    if (selectedCard?.imageUrls) {
      setCurrentImageIndex(prev => (prev + 1) % selectedCard.imageUrls.length);
      setRotation(0);
      setZoomLevel(1);
    }
  }, [selectedCard]);

  const handlePrevImage = useCallback(() => {
    if (selectedCard?.imageUrls) {
      setCurrentImageIndex(prev =>
        prev === 0 ? selectedCard.imageUrls.length - 1 : prev - 1
      );
      setRotation(0);
      setZoomLevel(1);
    }
  }, [selectedCard]);

  // Navigate to the correct add/edit route based on role
  // const handleAddNew = useCallback(() => {
  //   if (isAdmin) {
  //     navigate('/admin/expenses/new');
  //   } else {
  //     navigate('/accounting/expenses/new');
  //   }
  // }, [navigate, isAdmin]);


  // Navigate to the correct add/edit route based on role
  const handleAddNew = useCallback(() => {
    navigate('/accounting/expenses/new');

  }, [navigate]);
  // const handleEdit = useCallback((id) => {
  //   if (isAdmin) {
  //     navigate(`/admin/expenses/edit/${id}`);
  //   } else {
  //     navigate(`/accounting/expenses/edit/${id}`);
  //   }
  // }, [navigate, isAdmin]);
  const handleEdit = useCallback((id) => {
    navigate(`/accounting/expenses/edit/${id}`);
  }, [navigate]);

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = !searchTerm ||
      (expense.description && expense.description.toLowerCase().includes(searchTerm)) ||
      (expense.claimBy && expense.claimBy.toLowerCase().includes(searchTerm)) ||
      (expense.expenseType && expense.expenseType.toLowerCase().includes(searchTerm)) ||
      (expense.amount && expense.amount.toString().includes(searchTerm));

    const matchesStatus = statusFilter === 'All' || expense.status === statusFilter;
    const matchesType = typeFilter === 'All' ||
      (typeFilter === 'Expense' && expense.type === 'expense') ||
      (typeFilter === 'Voucher' && expense.type === 'voucher');

    // New filters
    const matchesDate = !dateFilter ||
      (expense.date && expense.date.includes(dateFilter)) ||
      (expense.createdAt && new Date(expense.createdAt?.toDate?.() || expense.createdAt).toLocaleDateString().includes(dateFilter));

    const matchesClaimant = !claimantFilter ||
      (expense.claimBy && expense.claimBy.toLowerCase().includes(claimantFilter.toLowerCase()));

    return matchesSearch && matchesStatus && matchesType && matchesDate && matchesClaimant;
  });

  const getStatusChip = (expense) => {
    const colors = getStatusColor(expense.status);
    const statusMessage = expense.status === 'Approved'
      ? expense.approvalComment || 'Approved'
      : expense.status === 'Rejected'
        ? expense.rejectionComment || 'Rejected'
        : expense.status;

    // Create a tooltip with the comment if it exists
    const statusChip = (
      <Chip
        label={expense.status}
        size="small"
        sx={{
          backgroundColor: colors.bgcolor,
          color: colors.color,
          fontWeight: 500,
          minWidth: 100,
          justifyContent: 'center',
          cursor: 'default',
          '&:hover': {
            backgroundColor: colors.bgcolor
          },
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 150
        }}
      />
    );

    // Show tooltip for both approved and rejected statuses if there's a comment
    const hasApprovalComment = expense.status === 'Approved' && expense.approvalComment;
    const hasRejectionComment = expense.status === 'Rejected' && expense.rejectionComment;

    const chipWithTooltip = hasApprovalComment || hasRejectionComment ? (
      <Tooltip
        title={
          <div style={{ whiteSpace: 'pre-line', maxWidth: 300 }}>
            <div><strong>Status:</strong> {expense.status}</div>
            <div><strong>By:</strong> {expense.status === 'Approved' ? expense.approvedBy : expense.rejectedBy}</div>
            <div><strong>On:</strong> {formatDate(expense.status === 'Approved' ? expense.approvedAt : expense.rejectedAt)}</div>
            <div><strong>Comment:</strong> {expense.status === 'Approved' ? expense.approvalComment : expense.rejectionComment}</div>
          </div>
        }
        arrow
        placement="top"
      >
        {statusChip}
      </Tooltip>
    ) : statusChip;

    if (isAdmin) {
      return (
        <FormControl size="small" variant="outlined" fullWidth>
          <Select
            value={expense.status}
            onChange={(e) => handleStatusChange(expense.id, e.target.value)}
            sx={{
              backgroundColor: colors.bgcolor,
              color: colors.color,
              fontWeight: 500,
              minWidth: 100,
              height: '32px',
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '6px 32px 6px 12px'
              }
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  marginTop: 8,
                  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)'
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
          >
            {statusOptions.filter(opt => opt !== 'All').map((status) => (
              <MenuItem
                key={status}
                value={status}
                sx={{
                  backgroundColor: status === expense.status ? '#f5f5f5' : 'white',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  },
                  minWidth: 120,
                  justifyContent: 'center'
                }}
              >
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // For non-admin users, show read-only chip with tooltip if there's a comment
    return chipWithTooltip;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography>Loading cards...</Typography>
      </Box>
    );
  }

  // Helper: pick the most likely image URL from a card object (checks many possible field names and nested objects)
  const getPreviewImage = (card) => {
    if (!card) return null;

    // direct common fields
    const direct = [
      card.imageUrl,
      card.voucherImageUrl,
      card.voucherImage,
      card.voucher_image,
      card.proofImage,
      card.proof_image,
      card.receiptUrl,
      card.image,
      card.url,
      card.photoUrl,
      card.displayImage
    ];

    for (const v of direct) if (v) return v;

    // arrays (use currentImageIndex if available)
    if (card.imageUrls && card.imageUrls.length) return card.imageUrls[currentImageIndex] || card.imageUrls[0];
    if (card.voucherImageUrls && card.voucherImageUrls.length) return card.voucherImageUrls[currentImageIndex] || card.voucherImageUrls[0];

    // nested structures
    if (card.voucher && (card.voucher.imageUrl || card.voucher.image)) return card.voucher.imageUrl || card.voucher.image;
    if (card.voucherDetails && (card.voucherDetails.imageUrl || card.voucherDetails.image)) return card.voucherDetails.imageUrl || card.voucherDetails.image;
    if (card.vouchers && card.vouchers.length) {
      const v = card.vouchers[currentImageIndex] || card.vouchers[0];
      if (v && (v.imageUrl || v.image)) return v.imageUrl || v.image;
    }

    return null;
  };

  const previewImage = getPreviewImage(selectedCard);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', borderRadius: 2, boxShadow: 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          mb: 3
        }}>
          <Typography component="h2" variant="h5" color="primary" sx={{
            fontWeight: 'bold',
            mb: { xs: 2, md: 0 }
          }}>
            Expense Booking
          </Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            width: { xs: '100%', md: 'auto' },
            '& .MuiFormControl-root, & .MuiButton-root': {
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: '100%', md: 150 },
              mb: { xs: 1, md: 0 }
            }
          }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Supporting</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Supporting"
              >
                <MenuItem value="All">All Types</MenuItem>
                <MenuItem value="Expense">Bills</MenuItem>
                <MenuItem value="Voucher">Voucher</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Filter */}
            <TextField
              size="small"
              label="Date (DD-MM-YYYY)"
              placeholder="DD-MM-YYYY"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              sx={{ width: 150, mr: 1 }}
            />
        

            {/* Claimant Filter */}
            {/* <TextField
              size="small"
              label="Claimant"
              placeholder="Enter name"
              value={claimantFilter}
              onChange={(e) => setClaimantFilter(e.target.value)}
              sx={{ width: 150, mr: 1 }}
            /> */}
            {/* <TextField
              variant="outlined"
              size="small"
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  minWidth: 250
                }
              }}
            /> */}
            <Tooltip title="Add Expense" arrow>
              <IconButton
                color="primary"
                onClick={handleAddNew}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  mt: { xs: 1, md: 0 },
                  alignSelf: { xs: 'stretch', md: 'flex-start' },
                  height: '40px',
                  width: '40px',
                  borderRadius: 2
                }}
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 120 }}>Photo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 120 }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 150 }}>Claimant</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 120 }}>Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', minWidth: 120 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', minWidth: 150 }}>Expense Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right', minWidth: 150 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExpenses.length > 0 ? (
                filteredExpenses
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((expense, idx) => (
                    <TableRow
                      key={expense.id + '-' + idx}
                      hover
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <TableCell>
                        {expense && (expense.imageUrl || expense?.voucherImageUrl || expense?.imageUrls?.[0]) ? (
                          <Box
                            component="img"
                            src={expense.imageUrl || expense?.voucherImageUrl || expense?.imageUrls?.[0]}
                            alt={expense?.type === 'voucher' ? 'Voucher proof' : 'Expense receipt'}
                            onError={(e) => {
                              console.error('Error loading image:', {
                                src: e.target.src,
                                expenseId: expense?.id,
                                hasImageUrl: !!expense?.imageUrl,
                                hasVoucherImageUrl: !!expense?.voucherImageUrl,
                                hasImageUrls: expense?.imageUrls?.length > 0,
                                expense: expense
                              });
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                            sx={{
                              width: 90,
                              height: 80,
                              objectFit: 'contain',
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: expense.type === 'voucher' ? 'primary.main' : 'divider',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              boxShadow: 2,
                              p: 0.5,
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                zIndex: 2,
                                position: 'relative',
                                boxShadow: 4
                              }
                            }}
                            onClick={() => {
                              const imageSources = [
                                expense.imageUrl,
                                expense.voucherImageUrl,
                                ...(expense.imageUrls || [])
                              ].filter(Boolean);

                              const previewExpense = {
                                ...expense,
                                imageUrls: imageSources.length > 0 ? imageSources : [expense.imageUrl || expense.voucherImageUrl].filter(Boolean)
                              };
                              console.log('Previewing expense:', previewExpense);
                              handlePreview(previewExpense);
                            }}
                          />
                        ) : expense.type === 'voucher' ? (
                          <Box
                            sx={{
                              width: 90,
                              height: 80,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: 'primary.main',
                              backgroundColor: 'primary.light',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'scale(1.05)',
                                transition: 'transform 0.2s',
                              }
                            }}
                            onClick={() => handlePreview(expense)}
                          >
                            <Box component="span" sx={{ fontSize: 24, color: 'primary.contrastText' }}>V</Box>
                            <Typography variant="caption" sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                              VOUCHER
                            </Typography>
                          </Box>
                        ) : (expense.imageUrls && expense.imageUrls.length > 0) ? (
                          <Box
                            component="img"
                            src={expense.imageUrls[0]}
                            alt="Expense receipt"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                            sx={{
                              width: 90,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 100,
                              objectFit: 'cover',
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: 'divider',
                              cursor: 'pointer',
                              backgroundColor: 'grey.100',
                              boxShadow: 2,
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'scale(1.15)',
                                zIndex: 2,
                                position: 'relative',
                                boxShadow: 4
                              }
                            }}
                            onClick={() => handlePreview(expense)}
                          />
                        ) : expense.imageUrl ? (
                          <Box
                            component="img"
                            src={expense.imageUrl}
                            alt="Expense receipt"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                            sx={{
                              width: 60,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              cursor: 'pointer',
                              backgroundColor: 'grey.100',
                              '&:hover': {
                                transform: 'scale(1.5)',
                                transition: 'transform 0.2s',
                                zIndex: 1,
                                position: 'relative',
                                boxShadow: 3
                              }
                            }}
                            onClick={() => handlePreview(expense)}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.100',
                              borderRadius: 1,
                              color: 'text.secondary'
                            }}
                          >
                            <ImageIcon fontSize="small" />
                          </Box>
                        )}
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {expense.date || 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* Claimant */}
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {expense.claimBy || expense.businessName || expense.description || 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* Amount */}
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{
                            // fontFamily: 'monospace',
                            color: 'text.primary'
                          }}
                        >
                          {getCurrencySymbol(expense.currency || 'INR')}
                          {expense.amount?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {getStatusChip(expense)}
                      </TableCell>

                      {/* Expense Type */}
                      <TableCell align="center" sx={{ py: 1, px: 1, whiteSpace: 'nowrap' }}>
                        <Chip
                          label={expense.expenseType || 'Other'}
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: getTypeColor(expense.expenseType),
                            color: 'white',
                            border: '1.5px solid rgba(255, 255, 255, 0.3)',
                            fontWeight: 500,
                            minWidth: 'fit-content',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.43)',
                            '& .MuiChip-label': {
                              px: 1.5,
                              py: 0.5
                            },
                            '&:hover': {
                              opacity: 0.95,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                              transition: 'all 0.2s ease-in-out'
                            },
                            '&.MuiChip-outlined': {
                              borderColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell
                        align="center"
                        sx={{
                          py: 0.5,
                          px: 0.5,
                          minWidth: '120px',
                          '& .MuiIconButton-root': {
                            ml: 0.25,
                            '&:first-of-type': {
                              ml: 0
                            },
                            p: 0.25,
                            margin: 0,
                            minWidth: '32px',
                            height: '32px'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                          <IconButton
                            onClick={() => handlePreview(expense)}
                            size="small"
                            title="View details"
                            sx={{
                              '&:hover': { backgroundColor: 'primary.light', color: 'primary.contrastText' },
                              transition: 'all 0.2s'
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleEdit(expense.id)}
                            size="small"
                            title="Edit"
                            disabled={expense.status !== 'Raised'}
                            sx={{
                              '&:hover': { backgroundColor: 'info.light', color: 'info.contrastText' },
                              transition: 'all 0.2s',
                              opacity: expense.status !== 'Raised' ? 0.5 : 1
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(expense.id)}
                            size="small"
                            title="Delete"
                            disabled={!isAdmin || (expense.status !== 'Raised' && expense.status !== 'Rejected')}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'error.contrastText',
                                opacity: (isAdmin && (expense.status === 'Raised' || expense.status === 'Rejected')) ? 1 : 0.5
                              },
                              transition: 'all 0.2s',
                              opacity: (isAdmin && (expense.status === 'Raised' || expense.status === 'Rejected')) ? 1 : 0.5
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>

                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (

                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Search fontSize="large" color="disabled" />
                      <Typography color="text.secondary">
                        {searchTerm ? 'No matching expenses found' : 'No expenses added yet'}
                      </Typography>
                      {!searchTerm && (
                        <Button
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={handleAddNew}
                          sx={{ mt: 1 }}
                        >
                          Add Your First Expense
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredExpenses.length === 0 ? 0 : page * rowsPerPage + 1} to{' '}
            {Math.min((page + 1) * rowsPerPage, filteredExpenses.length)} of {filteredExpenses.length} entries
          </Typography>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredExpenses.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ '& .MuiTablePagination-toolbar': { padding: 0 } }}
          />
        </Box>
      </Paper>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: '90vw',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'primary.main',
            color: 'white',
            py: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Visibility sx={{ mr: 1 }} />
            <span>Expense Details</span>
          </Box>
          <IconButton
            onClick={handleClosePreview}
            sx={{ color: 'white' }}
          >
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {selectedCard && (
            <Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    {/* Expense Type / Voucher ID */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        {selectedCard.type === 'voucher' ? 'Voucher ID' : 'Expense Type'}
                      </Typography>
                      <Typography variant="body1">
                        {selectedCard.type === 'voucher'
                          ? selectedCard.id || 'N/A'
                          : selectedCard.expenseType || 'N/A'}
                      </Typography>
                    </Box>

                    {/* Title / Business Name */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        {selectedCard.type === 'voucher' ? 'Title' : 'Business Name'}
                      </Typography>
                      <Typography variant="body1">
                        {selectedCard.businessName || selectedCard.title || 'N/A'}
                      </Typography>
                    </Box>

                    {/* Claim Information */}
                    {(selectedCard.claimBy || selectedCard.claimOn) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Claim Information
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {selectedCard.claimBy && (
                            <Box>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Claim By
                              </Typography>
                              <Typography variant="body2">
                                {selectedCard.claimBy}
                              </Typography>
                            </Box>
                          )}
                          {selectedCard.claimOn && (
                            <Box>

                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                  </Grid>



                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    {/* Amount */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Amount
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" color={selectedCard.type === 'voucher' ? 'primary' : 'inherit'}>
                          {getCurrencySymbol(selectedCard.currency || 'INR')}{parseFloat(selectedCard.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        {selectedCard.currency && selectedCard.currency !== 'INR' && (
                          <Typography variant="caption" color="textSecondary">
                            ({currencies.find(c => c.code === selectedCard.currency)?.name || selectedCard.currency})
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Date */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        {selectedCard.type === 'voucher' ? 'Issued On' : 'Expense Date'}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {formatDate(selectedCard.date)}
                      </Typography>

                      {/* Status */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Status
                        </Typography>
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          color:
                            selectedCard.status === 'Approved' ? 'success.main' :
                              selectedCard.status === 'Rejected' ? 'error.main' :
                                selectedCard.status === 'Verified' ? 'info.main' : 'text.primary',
                          bgcolor:
                            selectedCard.status === 'Approved' ? 'success.50' :
                              selectedCard.status === 'Rejected' ? 'error.50' :
                                selectedCard.status === 'Verified' ? 'info.50' : 'grey.100',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor:
                            selectedCard.status === 'Approved' ? 'success.light' :
                              selectedCard.status === 'Rejected' ? 'error.light' :
                                selectedCard.status === 'Verified' ? 'info.light' : 'grey.300',
                          fontWeight: 'medium',
                          fontSize: '0.875rem',
                          minWidth: 100,
                          justifyContent: 'center',
                          '& .status-icon': {
                            fontSize: '1rem',
                            display: 'flex',
                            alignItems: 'center'
                          }
                        }}>
                          {selectedCard.status === 'Approved' && <CheckCircle className="status-icon" />}
                          {selectedCard.status === 'Rejected' && <Cancel className="status-icon" />}
                          {selectedCard.status === 'Verified' && <VerifiedUser className="status-icon" />}
                          {!['Approved', 'Rejected', 'Verified'].includes(selectedCard.status) && <HourglassEmpty className="status-icon" />}
                          <span style={{ marginLeft: '4px' }}>{selectedCard.status || 'Raised'}</span>
                        </Box>

                        {/* Approval Note Section */}
                        {selectedCard.status === 'Approved' && (selectedCard.approvalComment || selectedCard.approvedNote) && (
                          <Box sx={{
                            mt: 1,
                            p: 2,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 1,
                            borderLeft: '4px solid',
                            borderColor: 'success.main',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}>
                            <Typography variant="subtitle2" color="text.primary" gutterBottom>
                              Approval Note
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {selectedCard.approvalComment || selectedCard.approvedNote}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              {selectedCard.approvedBy && (
                                <Typography variant="caption" color="text.secondary">
                                  Approved by: {selectedCard.approvedBy}
                                </Typography>
                              )}
                              {selectedCard.approvedAt && (
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(selectedCard.approvedAt).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        {selectedCard.status === 'Rejected' && (selectedCard.rejectionComment || selectedCard.rejectionReason) && (
                          <Box sx={{
                            mt: 1,
                            p: 2,
                            backgroundColor: 'rgba(255, 0, 0, 0.05)',
                            borderRadius: 1,
                            borderLeft: '4px solid',
                            borderColor: 'error.main',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}>
                            <Typography variant="subtitle2" color="text.primary" gutterBottom>
                              Rejection Reason
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {selectedCard.rejectionComment || selectedCard.rejectionReason || 'No reason provided'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              {selectedCard.rejectedBy && (
                                <Typography variant="caption" color="text.secondary">
                                  Rejected by: {selectedCard.rejectedBy}
                                </Typography>
                              )}
                              {selectedCard.rejectedAt && (
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(selectedCard.rejectedAt).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Description / Additional Details */}
                    {selectedCard.description || selectedCard.additionalDetails ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          {selectedCard.type === 'voucher' ? 'Description' : 'Additional Details'}
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                          {selectedCard.description || selectedCard.additionalDetails || 'N/A'}
                        </Typography>
                      </Box>
                    ) : null}

                    <DialogContent sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                        {/* Approval/Rejection Section */}
                        {(infoDialog.expense?.approvedBy || infoDialog.expense?.rejectedBy) && (
                          <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              {infoDialog.expense.approvedBy && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                                    APPROVED BY
                                  </Typography>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>
                                    {infoDialog.expense.approvedBy}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {infoDialog.expense.approvedAt?.toDate ?
                                      formatDate(infoDialog.expense.approvedAt.toDate()) : 'N/A'}
                                  </Typography>
                                </Box>
                              )}
                              {infoDialog.expense.rejectedBy && (
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                                    REJECTED BY
                                  </Typography>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>
                                    {infoDialog.expense.rejectedBy}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {infoDialog.expense.rejectedAt?.toDate ?
                                      formatDate(infoDialog.expense.rejectedAt.toDate()) : 'N/A'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            {infoDialog.expense.rejectionComment && (
                              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                                  REJECTION REASON
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-line' }}>
                                  {infoDialog.expense.rejectionComment}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>

                    </DialogContent>

                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: selectedCard.type === 'voucher' ? 'primary.50' : 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {selectedCard.type === 'voucher' ? 'Voucher Details' : 'Receipt / Bill'}
                    {selectedCard?.imageUrls?.length > 1 ? `(${currentImageIndex + 1}/${selectedCard.imageUrls.length})` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedCard?.imageUrls?.length > 1 && (
                      <>
                        <IconButton
                          onClick={handlePrevImage}
                          size="small"
                          title="Previous image"
                        >
                          <ArrowBackIosNew fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={handleNextImage}
                          size="small"
                          title="Next image"
                        >
                          <ArrowForwardIos fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    <IconButton
                      onClick={handleRotateLeft}
                      size="small"
                      title="Rotate left"
                    >
                      <RotateLeft />
                    </IconButton>
                    <IconButton
                      onClick={handleRotateRight}
                      size="small"
                      title="Rotate right"
                    >
                      <RotateRight />
                    </IconButton>
                    <IconButton
                      onClick={handleZoomOut}
                      size="small"
                      disabled={zoomLevel <= 0.5}
                      title="Zoom out"
                    >
                      <ZoomOut />
                    </IconButton>
                    <IconButton
                      onClick={handleZoomIn}
                      size="small"
                      disabled={zoomLevel >= 3}
                      title="Zoom in"
                    >
                      <ZoomIn />
                    </IconButton>
                    {/* <IconButton 
                      onClick={() => handleDownload(selectedCard.imageUrls?.[currentImageIndex] || selectedCard.imageUrl)} 
                      size="small"
                      title="Download image"
                    >
                      <Download />
                    </IconButton> */}
                  </Box>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'white',
                    overflow: 'auto',
                    maxHeight: '60vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                    gap: 2,
                    flexWrap: 'wrap'
                  }}
                >
                  {previewImage ? (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: '300px',
                        bgcolor: 'rgba(0,0,0,0.05)'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                          transition: 'transform 0.3s ease',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          padding: 2
                        }}
                      >
                        <img
                          src={previewImage}
                          alt={`Voucher proof`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '60vh',
                            objectFit: 'contain',
                            borderRadius: 4,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    </Box>
                  ) : (selectedCard?.imageUrls && selectedCard.imageUrls.length > 0) ? (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: '300px',
                        bgcolor: 'rgba(0,0,0,0.05)'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                          transition: 'transform 0.3s ease',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          padding: 2
                        }}
                      >
                        <img
                          src={previewImage}
                          alt={`Expense receipt ${currentImageIndex + 1}`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '60vh',
                            objectFit: 'contain',
                            borderRadius: 4,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" color="textSecondary">No photo available</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

       
        <Box style={{ opacity: 0.8, marginBottom: 0 }}>

          <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
            Created by: {userInfo.createdBy}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
            Created on: {userInfo.createdAt}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
            Last updated by: {userInfo.updatedBy}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
            Last updated on: {userInfo.updatedAt}
          </Typography>
        </Box>
        <DialogActions sx={{ p: 1, borderTop: 1, borderColor: 'divider', position: 'center' }}>

          <Button
            onClick={handleClosePreview}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
          >
            Close
          </Button>
          {selectedCard?.type === 'voucher' && previewImage && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                // Print a full voucher receipt
                const printWindow = window.open('', '_blank');
                const receiptHtml = `
                  <html>
                  <head>
                    <title>Print Voucher</title>
                    <style>
                      body { font-family: Arial, sans-serif; margin: 0; padding: 24px; background: #fff; }
                      .receipt-container { max-width: 480px; margin: auto; border: 1px solid #eee; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); padding: 24px; }
                      .header { text-align: center; margin-bottom: 16px; }
                      .voucher-img { display: block; margin: 16px auto; max-width: 320px; max-height: 240px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
                      .details { margin-bottom: 12px; }
                      .label { font-weight: bold; color: #333; }
                      .value { margin-left: 8px; color: #444; }
                      .status { font-weight: bold; padding: 4px 12px; border-radius: 6px; background: #e8f5e9; color: #2e7d32; display: inline-block; margin-bottom: 8px; }
                      .footer { text-align: center; margin-top: 24px; font-size: 12px; color: #888; }
                    </style>
                  </head>
                  <body>
                    <div class='receipt-container'>
                      <div class='header'>
                        <h2>Voucher Receipt</h2>
                        <div class='status'>${selectedCard.status || ''}</div>
                      </div>
                      <div class='details'><span class='label'>Voucher ID:</span><span class='value'>${selectedCard.id || ''}</span></div>
                      <div class='details'><span class='label'>Title:</span><span class='value'>${selectedCard.title || selectedCard.businessName || ''}</span></div>
                      <div class='details'><span class='label'>Amount:</span><span class='value'>${selectedCard.currency || 'INR'} ${parseFloat(selectedCard.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                      <div class='details'><span class='label'>Date:</span><span class='value'>${selectedCard.date || ''}</span></div>
                      <div class='details'><span class='label'>Description:</span><span class='value'>${selectedCard.description || ''}</span></div>
                      ${previewImage ? `<img class='voucher-img' src='${previewImage}' alt='Voucher Proof' />` : ''}
                      ${selectedCard.status === 'Approved' && selectedCard.approvalComment ? `<div class='details'><span class='label'>Approval Note:</span><span class='value'>${selectedCard.approvalComment}</span></div>` : ''}
                      ${selectedCard.status === 'Rejected' && selectedCard.rejectionComment ? `<div class='details'><span class='label'>Rejection Reason:</span><span class='value'>${selectedCard.rejectionComment}</span></div>` : ''}
                      <div class='footer'>Printed on ${new Date().toLocaleString()}</div>
                    </div>
                  </body>
                  </html>
                `;
                printWindow.document.write(receiptHtml);
                printWindow.document.close();
                printWindow.focus();
                printWindow.print();
              }}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Print Voucher
            </Button>
          )}
          {selectedCard && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleClosePreview();
                handleEdit(selectedCard.id);
              }}
              startIcon={<Edit />}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
              disabled={selectedCard.status !== 'Raised'}
            >
              Edit Expense
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog
        open={commentDialog.open}
        onClose={() => setCommentDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {commentDialog.status === 'Approved' ? 'Add Approval Comment' : 'Add Rejection Reason'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={commentDialog.status === 'Approved' ? 'Approval Comment' : 'Rejection Reason'}
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={commentDialog.comment}
            onChange={(e) => setCommentDialog(prev => ({ ...prev, comment: e.target.value }))}
            placeholder={commentDialog.status === 'Approved'
              ? 'Enter your approval comments here...'
              : 'Please provide a reason for rejection...'}
            helperText={commentDialog.status === 'Rejected' ? 'Please provide a reason for rejection' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider', align: "center" }}>
          <Button
            onClick={() => setCommentDialog(prev => ({ ...prev, open: false }))}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCommentSubmit}
            variant="contained"
            color={commentDialog.status === 'Approved' ? 'primary' : 'error'}
            disabled={!commentDialog.comment.trim()}
          >
            {commentDialog.status === 'Approved' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Info Dialog */}
      <Dialog
        open={infoDialog.open}
        onClose={handleCloseInfo}
        maxWidth="sm"
        fullWidth
      >

      </Dialog>
    </Container>
  );
};

export default ListPage;